import type { LucideIconName } from "@/deps/ui-library";
/**
 * Navigation model, transcribed from design/App Nav.dc.html. `to` is a route in
 * this app; `planned` points at the generic stub for areas designed in other
 * files. `meta` is a count or a plan badge shown in mono on the right.
 */
export interface NavItem {
  label: string;
  icon: LucideIconName;
  to: string;
  /** The label of the run this item belongs to in its menu. */
  group?: string;
  meta?: string;
  permission?: string;
  /** The plan feature it belongs to, so the menu can show it locked (suite ADR-0096). */
  feature?: string;
}
export interface NavSection {
  id: "home" | "sales" | "purchases" | "banking" | "accounting" | "reports" | "settings";
  label: string;
  icon: LucideIconName;
  to?: string;
  items?: NavItem[];
  alignRight?: boolean;
}

const planned = (feature: string) => `/planned/${feature}`;

export const NAV: NavSection[] = [
  { id: "home", label: "Home", icon: "LayoutDashboard", to: "/" },
  {
    id: "sales",
    label: "Sales",
    icon: "FileText",
    items: [
      {
        group: "Documents",
        label: "Quotations",
        icon: "FilePenLine",
        to: "/sales/quotations",
        feature: "QUOTATIONS",
      },
      { group: "Documents", label: "Invoices", icon: "FileText", to: "/sales/invoices" },
      { group: "Documents", label: "Credit notes", icon: "FileMinus", to: "/sales/credit-notes" },
      { group: "Documents", label: "Receipts", icon: "ArrowDownLeft", to: "/sales/receipts" },
      { group: "Customers and items", label: "Customers", icon: "Users", to: "/sales/customers" },
      {
        group: "Customers and items",
        label: "Receivable analysis",
        icon: "ChartColumn",
        to: planned("reports"),
      },
      {
        group: "Customers and items",
        label: "Billing items",
        icon: "Package",
        to: "/sales/billing-items",
      },
    ],
  },
  {
    id: "purchases",
    label: "Purchases",
    icon: "Receipt",
    items: [
      {
        group: "Documents",
        label: "Purchase orders",
        icon: "ClipboardList",
        to: "/purchases/orders",
        feature: "PURCHASE_ORDERS",
      },
      { group: "Documents", label: "Bills", icon: "Receipt", to: "/purchases/bills" },
      { group: "Documents", label: "Debit notes", icon: "FilePlus", to: "/purchases/debit-notes" },
      { group: "Documents", label: "Payments", icon: "ArrowUpRight", to: "/purchases/payments" },
      { group: "Suppliers", label: "Suppliers", icon: "Building2", to: "/purchases/suppliers" },
      {
        group: "Suppliers",
        label: "Payable analysis",
        icon: "ChartColumn",
        to: planned("reports"),
      },
    ],
  },
  {
    id: "banking",
    label: "Banking",
    icon: "Landmark",
    items: [
      { label: "Bank accounts", icon: "Landmark", to: "/banking/accounts" },
      {
        label: "Reconciliation",
        icon: "GitMerge",
        to: "/banking/reconciliation",
        feature: "RECONCILIATION",
      },
    ],
  },
  {
    id: "accounting",
    label: "Accounting",
    icon: "BookOpen",
    items: [
      { group: "Entries", label: "Journals", icon: "BookOpen", to: "/accounting/journals" },
      {
        group: "Entries",
        label: "Fund transfers",
        icon: "ArrowLeftRight",
        to: "/banking/transfers",
      },
      { group: "Entries", label: "Approvals", icon: "SquareCheckBig", to: "/accounting/approvals" },
      { group: "Ledgers", label: "Employee ledgers", icon: "Users", to: "/accounting/employees" },
      {
        group: "Periods",
        label: "Period control",
        icon: "CalendarClock",
        to: "/accounting/periods",
      },
      {
        group: "Periods",
        label: "Year-end close",
        icon: "CalendarCheck",
        to: "/accounting/year-end",
      },
    ],
  },
  {
    id: "reports",
    label: "Reports",
    icon: "ChartColumn",
    alignRight: true,
    items: [
      { label: "All reports", icon: "LayoutGrid", to: "/reports" },
      {
        group: "Statements",
        label: "Profit and loss",
        icon: "TrendingUp",
        to: "/reports/profit-and-loss",
      },
      { group: "Statements", label: "Balance sheet", icon: "Scale", to: "/reports/balance-sheet" },
      { group: "Statements", label: "Cash flow", icon: "Waves", to: "/reports/cash-flow" },
      {
        group: "Statements",
        label: "Trial balance",
        icon: "ListTree",
        to: "/reports/trial-balance",
      },
      {
        group: "Statements",
        label: "General ledger",
        icon: "BookOpen",
        to: "/reports/general-ledger",
      },
      {
        group: "Receivables and payables",
        label: "Receivables ageing",
        icon: "ArrowDownLeft",
        to: "/reports/receivables-ageing",
      },
      {
        group: "Receivables and payables",
        label: "Payables ageing",
        icon: "ArrowUpRight",
        to: "/reports/payables-ageing",
      },
      { group: "Tax", label: "VAT return", icon: "Percent", to: "/reports/vat-return" },
      { group: "Tax", label: "TDS withheld", icon: "Banknote", to: "/reports/tds-withheld" },
      { group: "Reporting setup", label: "Account mapping", icon: "Tags", to: "/reports/mapping" },
      {
        group: "Reporting setup",
        label: "Report templates",
        icon: "LayoutList",
        to: "/reports/templates",
      },
      {
        group: "Reporting setup",
        label: "Reporting layer add-on",
        icon: "Sparkles",
        to: planned("reports"),
      },
    ],
  },
  {
    id: "settings",
    label: "Settings",
    icon: "Settings",
    alignRight: true,
    items: [
      { label: "All settings", icon: "LayoutGrid", to: "/settings" },
      {
        group: "Company and books",
        label: "Company profile",
        icon: "Building2",
        to: "/settings?tab=company",
      },
      {
        group: "Company and books",
        label: "Chart of accounts",
        icon: "ListTree",
        to: "/settings?tab=coa",
      },
      {
        group: "Company and books",
        label: "Numbering",
        icon: "Hash",
        to: "/settings?tab=numbering",
      },
      {
        group: "Company and books",
        label: "Approval rules",
        icon: "SquareCheckBig",
        to: "/settings/approval-rules",
      },
      {
        group: "Company and books",
        label: "Audit trail",
        icon: "History",
        to: "/settings?tab=audit",
      },
      {
        group: "Documents",
        label: "Invoicing setup",
        icon: "Settings2",
        to: "/settings/invoicing",
      },
      { group: "Documents", label: "Document branding", icon: "Palette", to: "/settings/branding" },
      {
        group: "Documents",
        label: "Email templates",
        icon: "Mail",
        to: "/settings/email-templates",
      },
      {
        group: "Localization · Nepal",
        label: "Country pack",
        icon: "Globe",
        to: "/settings/localization",
      },
      {
        group: "Localization · Nepal",
        label: "Tax codes",
        icon: "Percent",
        to: "/settings?tab=tax",
      },
      { group: "Team and security", label: "Users", icon: "Users", to: "/settings?tab=users" },
      {
        group: "Team and security",
        label: "Roles and permissions",
        icon: "ShieldCheck",
        to: "/settings?tab=roles",
      },
      { group: "Account", label: "Subscription", icon: "CreditCard", to: "/settings/subscription" },
      {
        group: "Compliance and connections",
        label: "Compliance",
        icon: "ShieldCheck",
        to: planned("compliance"),
      },
      {
        group: "Compliance and connections",
        label: "Integrations",
        icon: "Link2",
        to: planned("integrations"),
      },
    ],
  },
];

/** A section's items as runs under their group label, in the order written. */
export function groupsOf(items: NavItem[]): Array<{ label?: string; items: NavItem[] }> {
  const groups: Array<{ label?: string; items: NavItem[] }> = [];
  for (const item of items) {
    const last = groups.at(-1);
    if (last && last.label === item.group) last.items.push(item);
    else groups.push({ label: item.group, items: [item] });
  }
  return groups;
}

/** The “+ New document” menu. */
export const NEW_DOCUMENT_GROUPS: Array<{ label: string; items: NavItem[] }> = [
  {
    label: "Sales",
    items: [
      {
        label: "New quotation",
        icon: "FilePenLine",
        to: "/sales/quotations/new",
        permission: "sales.quotations.create",
        feature: "QUOTATIONS",
      },
      {
        label: "New invoice",
        icon: "FileText",
        to: "/sales/invoices/new",
        permission: "sales.invoices.create",
      },
      {
        label: "New credit note",
        icon: "FileMinus",
        to: "/sales/credit-notes/new",
        permission: "sales.creditnotes.create",
      },
      {
        label: "New receipt",
        icon: "ArrowDownLeft",
        to: "/sales/receipts/new",
        permission: "sales.receipts.create",
      },
    ],
  },
  {
    label: "Purchases",
    items: [
      {
        label: "New purchase order",
        icon: "ClipboardList",
        to: "/purchases/orders/new",
        permission: "purchases.orders.create",
        feature: "PURCHASE_ORDERS",
      },
      {
        label: "New bill",
        icon: "Receipt",
        to: "/purchases/bills/new",
        permission: "purchases.bills.create",
      },
      {
        label: "New debit note",
        icon: "FilePlus",
        to: "/purchases/debit-notes/new",
        permission: "purchases.debitnotes.create",
      },
      {
        label: "New supplier payment",
        icon: "ArrowUpRight",
        to: "/purchases/payments/new",
        permission: "purchases.payments.create",
      },
    ],
  },
  {
    label: "Accounting",
    items: [
      {
        label: "New journal voucher",
        icon: "BookOpen",
        to: "/accounting/journals/new",
        permission: "accounting.journals.create",
      },
      {
        label: "New fund transfer",
        icon: "ArrowLeftRight",
        to: "/banking/transfers/new",
        permission: "banking.transfers.create",
      },
    ],
  },
];

/** Which section is active for a given pathname. */
export function activeSection(pathname: string): NavSection["id"] {
  if (pathname === "/") return "home";
  if (pathname.startsWith("/sales")) return "sales";
  if (pathname.startsWith("/purchases")) return "purchases";
  if (pathname.startsWith("/banking")) return "banking";
  if (pathname.startsWith("/accounting")) return "accounting";
  if (pathname.startsWith("/reports")) return "reports";
  if (pathname.startsWith("/settings")) return "settings";
  return "home";
}
